(() => {
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const instances = new Set();

  const setButtonState = (instance) => {
    const playing = !instance.video.paused;
    instance.button.setAttribute('aria-pressed', String(playing));
    instance.button.setAttribute('aria-label', `${playing ? 'Pause' : 'Play'} real road footage`);
  };

  const loadVideo = (instance) => {
    if (instance.loaded) return;

    instance.source.src = instance.source.dataset.src;
    instance.video.load();
    instance.loaded = true;
  };

  const playVideo = (instance, userInitiated = false) => {
    loadVideo(instance);
    if (userInitiated) instance.userPaused = false;
    instance.video.play().catch(() => setButtonState(instance));
  };

  const syncMotionPreference = () => {
    instances.forEach((instance) => {
      if (reducedMotion.matches) {
        instance.video.pause();
        if (instance.loaded) instance.video.currentTime = 0;
      } else if (instance.inView && !instance.userPaused && document.visibilityState === 'visible') {
        playVideo(instance);
      }
      setButtonState(instance);
    });
  };

  const initVideo = (wrapper) => {
    if (wrapper.dataset.dashcamVideoReady === 'true') return;

    const video = wrapper.querySelector('[data-dashcam-video-element]');
    const source = video?.querySelector('source[data-src]');
    const button = wrapper.querySelector('[data-dashcam-video-toggle]');
    if (!video || !source || !button) return;

    wrapper.dataset.dashcamVideoReady = 'true';
    video.muted = true;

    const instance = {
      wrapper,
      video,
      source,
      button,
      loaded: false,
      inView: false,
      userPaused: false,
    };

    instances.add(instance);

    button.addEventListener('click', () => {
      if (video.paused) {
        playVideo(instance, true);
      } else {
        instance.userPaused = true;
        video.pause();
      }
    });

    video.addEventListener('play', () => setButtonState(instance));
    video.addEventListener('pause', () => setButtonState(instance));

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        instance.inView = entry.isIntersecting;
        if (!entry.isIntersecting) {
          video.pause();
          return;
        }

        if (!reducedMotion.matches && !instance.userPaused && document.visibilityState === 'visible') {
          playVideo(instance);
        }
      });
    }, {
      rootMargin: '240px 0px',
      threshold: 0.12,
    });

    observer.observe(wrapper);
    setButtonState(instance);
  };

  const initReturnButton = (button) => {
    if (button.dataset.dashcamReturnBound === 'true') return;
    button.dataset.dashcamReturnBound = 'true';

    button.addEventListener('click', () => {
      const purchaseTarget = document.querySelector('[id^="ProductInformation-"]')
        || document.querySelector('[id^="MainProduct-"]')
        || document.querySelector('product-form-component.product-form')
        || document.querySelector('form[id^="product-form-"]');

      if (!purchaseTarget) return;

      purchaseTarget.scrollIntoView({
        behavior: reducedMotion.matches ? 'auto' : 'smooth',
        block: 'start',
      });

      window.setTimeout(() => {
        const focusTarget = purchaseTarget.querySelector('form[id^="product-form-"] button[type="submit"]:not([disabled])')
          || purchaseTarget.querySelector('button[type="submit"]:not([disabled])')
          || purchaseTarget;

        if (focusTarget === purchaseTarget && !purchaseTarget.hasAttribute('tabindex')) {
          purchaseTarget.setAttribute('tabindex', '-1');
        }

        focusTarget.focus({ preventScroll: true });
      }, reducedMotion.matches ? 0 : 420);
    });
  };

  const init = (root = document) => {
    if (root.matches?.('.dashcam-pdp [data-dashcam-video]')) initVideo(root);
    root.querySelectorAll?.('.dashcam-pdp [data-dashcam-video]').forEach(initVideo);

    if (root.matches?.('.dashcam-pdp [data-dashcam-return-to-buy]')) initReturnButton(root);
    root.querySelectorAll?.('.dashcam-pdp [data-dashcam-return-to-buy]').forEach(initReturnButton);
  };

  const ready = () => init(document);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', ready, { once: true });
  } else {
    ready();
  }

  reducedMotion.addEventListener('change', syncMotionPreference);
  document.addEventListener('visibilitychange', syncMotionPreference);
  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
